import { useState, useEffect, useMemo } from 'react'
import { useTranslation } from 'react-i18next'
import { Input } from '../components/Input'
import { Select } from '../components/Select'
import { FormNavigation } from '../components/FormNavigation'
import { useOnboardingStore } from '../store/onboardingStore'
import { useTenant } from '../context/TenantContext'
import {
  validateAddress,
  validateCity,
  sanitizeCity,
  formatPostalCode,
  validatePostalCode,
  streetEndsWithHouseNumber,
} from '../utils/validation'
import { MapPin } from 'lucide-react'
import { AddressAutocomplete } from '../components/AddressAutocomplete'
import { InfoBox } from '../components/InfoBox'
import { useTestMode } from '../context/TestModeContext'
import { geocodeService, AddressSuggestion } from '../services/api'
import { logger } from '../utils/logger'
import type { StepProps } from '../types'
import { buildCountriesList } from '../data/countries'
import { STATES_BY_COUNTRY } from '../data/states'
import { getPostalCodeExample, POSTAL_CODE_CONFIGS } from '../data/postalCodes'
import { ItalyDomicileFields } from './italy/ItalyDomicileFields'
import { ITALIAN_PROVINCE_OPTIONS, regionForProvince } from '../data/italyProvinces'

export function Step3AddressData({ onNext, onBack }: StepProps) {
  const { t, i18n } = useTranslation()
  const { tenant } = useTenant()
  const isItaly = tenant?.country === 'IT'
  const countries = buildCountriesList(i18n.language, t('step3.otherCountry'))
  const {
    addressData,
    setAddressData,
    addressCheck,
    setAddressCheck,
    personalData,
  } = useOnboardingStore()
  const [errors, setErrors] = useState<{
    address?: string
    houseNumber?: string
    city?: string
    state?: string
    postalCode?: string
  }>({})

  // Address validation feature: per-org mode ('off' | 'test' | 'on').
  // 'test' shows it only in test mode — safe evaluation without touching patients.
  const isTestMode = useTestMode()
  const addressMode = tenant?.addressValidation ?? 'test'

  // Split-field address (street | Nr.) with the combined `address` kept as a
  // mirror for verify calls and legacy consumers. Drafts from before the
  // split are migrated once on mount (client-side first-digit split).
  const setStreetAndNumber = (patch: { street?: string; houseNumber?: string; noHouseNumber?: boolean }) => {
    let street = patch.street ?? addressData.street ?? ''
    const noNr = patch.noHouseNumber ?? addressData.noHouseNumber ?? false
    const nr = noNr ? '' : (patch.houseNumber ?? addressData.houseNumber ?? '')
    // Patients often paste the whole address into the street field and then
    // fill Nr. too ("Viale Monza, 38" + "38"). Drop the duplicated trailing
    // number so street stays a street and the mirror doesn't read "… 38 38".
    if (nr.trim()) {
      const dedup = street.replace(new RegExp(`[\\s,]+${nr.trim().replace(/[.*+?^${}()|[\]\\]/g, '\\$&')}$`), '')
      if (dedup.trim()) street = dedup
    }
    setAddressData({ ...patch, street, ...(noNr ? { houseNumber: '' } : {}), address: [street.trim(), nr.trim()].filter(Boolean).join(' ') })
  }
  useEffect(() => {
    if (addressData.address && !addressData.street) {
      const m = addressData.address.match(/^(.*?)[\s,]+(\d.*)$/)
      setAddressData(m
        ? { street: m[1].trim(), houseNumber: m[2].trim() }
        : { street: addressData.address.trim() })
    }
  }, []) // eslint-disable-line react-hooks/exhaustive-deps
  const addressFeatureEnabled = addressMode === 'on' || (addressMode === 'test' && isTestMode)
  const [isVerifying, setIsVerifying] = useState(false)
  const [softWarn, setSoftWarn] = useState<{ suggestion?: AddressSuggestion } | null>(null)

  // Set default country from tenant config if not yet set by user
  useEffect(() => {
    if (tenant?.defaultCountry && !addressData.address && !addressData.city) {
      // Only override if user hasn't started filling in address yet
      const tenantCountry = tenant.defaultCountry
      if (tenantCountry !== addressData.country) {
        setAddressData({ country: tenantCountry })
      }
    }
  }, [tenant?.defaultCountry]) // eslint-disable-line react-hooks/exhaustive-deps

  // Get available states for selected country.
  //
  // The hardcoded labels are German exonyms (Lazio → "Latium", Cataluña →
  // "Katalonien"), which is right for the German UI but wrong everywhere else:
  // an Italian patient must not be shown German names for Italian regions.
  // `value` already holds the native name, so outside the German UI we simply
  // display that — the endonym is what appears on the patient's own documents.
  const availableStates = useMemo(
    () => {
      const list = STATES_BY_COUNTRY[addressData.country] || []
      if (i18n.language === 'de') return list
      return list.map((s) => ({ ...s, label: s.value }))
    },
    [addressData.country, i18n.language]
  )
  const hasStateDropdown = availableStates.length > 0

  // Italy org + Italian residence: the patient picks a PROVINCE from the coded
  // list; the region is derived automatically and kept out of the UI
  // (change request 2026-08-25, §3.3–3.4). `state` keeps holding the region so
  // the DB/PVS sync shape is unchanged.
  const usesProvinceDropdown = isItaly && addressData.country === 'Italy'

  // Reset state when country changes if current state is not valid for new country
  useEffect(() => {
    if (hasStateDropdown) {
      const isCurrentStateValid = availableStates.some(s => s.value === addressData.state)
      if (!isCurrentStateValid && addressData.state) {
        setAddressData({ state: '' })
      }
    }
  }, [addressData.country, addressData.state, availableStates, hasStateDropdown, setAddressData])

  const validateField = (field: string, value: string): string | undefined => {
    switch (field) {
      case 'address':
        return validateAddress(value)
      case 'houseNumber':
        if (addressData.noHouseNumber) return undefined
        if (!value.trim()) return t('validation.houseNumberRequired')
        if (value.trim().length > 20) return t('validation.houseNumberTooLong')
        return undefined

      case 'city':
        return validateCity(value, t('step3.city'))

      case 'state':
        if (!value.trim()) return t('step3.stateValidation')
        return undefined

      case 'postalCode':
        return validatePostalCode(value, addressData.country)

      default:
        return undefined
    }
  }

  const handleBlur = (field: string, value: string) => {
    let processedValue = value

    // Auto-format city names
    if (field === 'city' && value.trim()) {
      const sanitized = sanitizeCity(value)
      const formatted = sanitized
        .toLowerCase()
        .replace(/(^|[\s\-'])./g, (match) => match.toUpperCase())
      if (formatted !== value) {
        setAddressData({ city: formatted })
        processedValue = formatted
      }
    }

    // Auto-format postal codes
    if (field === 'postalCode' && value.trim()) {
      const formatted = formatPostalCode(value, addressData.country)
      if (formatted !== value) {
        setAddressData({ postalCode: formatted })
        processedValue = formatted
      }
    }

    const error = validateField(field, processedValue)
    setErrors(prev => ({ ...prev, [field]: error }))
  }

  const handleChange = (field: string, value: string) => {
    setAddressData({ [field]: value })
    // Clear error when user starts typing
    if (errors[field as keyof typeof errors]) {
      setErrors(prev => ({ ...prev, [field]: undefined }))
    }
  }

  // Normalize for fuzzy state matching (suggestion state name → dropdown value)
  const normalize = (v: string) => v.toLowerCase().normalize('NFD').replace(/[\u0300-\u036f]/g, '')

  const handleSuggestionSelect = (suggestion: AddressSuggestion) => {
    const address = [suggestion.street, suggestion.housenumber].filter(Boolean).join(' ')
    const update: Record<string, string> = {
      address,
      street: suggestion.street || address,
      houseNumber: suggestion.housenumber || '',
    }
    if (suggestion.postcode) update.postalCode = suggestion.postcode
    if (suggestion.city) update.city = suggestion.city
    // Photon omits `state` for city-states (Berlin, Hamburg, Bremen) — there
    // the city IS the Bundesland, so fall back to matching the city
    const stateCandidate = suggestion.state || suggestion.city
    if (stateCandidate) {
      const match = availableStates.find(
        s => normalize(s.value) === normalize(stateCandidate) || normalize(s.label) === normalize(stateCandidate)
      )
      if (match) update.state = match.value
    }
    setAddressData(update)
    // A picked suggestion IS verified data (setAddressData cleared any old check)
    setAddressCheck({
      status: suggestion.housenumber ? 'exact' : 'street',
      normalized: suggestion.label,
      lat: suggestion.lat,
      lon: suggestion.lon,
    })
    setSoftWarn(null)
    setErrors({})
  }

  const applySuggestion = () => {
    if (softWarn?.suggestion) handleSuggestionSelect(softWarn.suggestion)
  }

  const handleCountryChange = (value: string) => {
    // Leaving Italy invalidates a previously selected province
    setAddressData(value === 'Italy' ? { country: value } : { country: value, province: '' })
    // Clear postal code error when country changes (different validation)
    if (errors.postalCode) {
      setErrors(prev => ({ ...prev, postalCode: undefined }))
    }
  }

  const isValid =
    (addressData.street || '').trim().length >= 2 &&
    (addressData.noHouseNumber || (addressData.houseNumber || '').trim().length >= 1) &&
    addressData.address.trim().length >= 5 &&
    addressData.city.trim().length >= 2 &&
    addressData.state.trim() &&
    (!usesProvinceDropdown || !!addressData.province) &&
    addressData.postalCode.trim() &&
    !validateField('postalCode', addressData.postalCode)

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault()

    // Validate all fields on submit
    const newErrors = {
      address: validateField('address', addressData.street || addressData.address),
      houseNumber: validateField('houseNumber', addressData.houseNumber || ''),
      city: validateField('city', addressData.city),
      state: validateField('state', addressData.state),
      postalCode: validateField('postalCode', addressData.postalCode),
    }

    setErrors(newErrors)

    const hasErrors = Object.values(newErrors).some(error => error !== undefined)
    if (hasErrors) return

    // Best-effort existence check (org-gated). NEVER blocks: outages and
    // OSM data gaps degrade to format-only validation.
    if (addressFeatureEnabled && !addressCheck) {
      // Second submit while the warning is visible = patient's explicit choice
      if (softWarn) {
        setAddressCheck({ status: 'unverified' })
        setSoftWarn(null)
        onNext()
        return
      }
      setIsVerifying(true)
      try {
        const result = await geocodeService.verify({
          address: addressData.address,
          postalCode: addressData.postalCode,
          city: addressData.city,
          country: addressData.country,
        })
        if (!result.available) {
          onNext() // geocoder down — proceed silently
          return
        }
        if (result.status === 'exact' || result.status === 'street') {
          setAddressCheck({
            status: result.status,
            normalized: result.match?.label,
            lat: result.match?.lat,
            lon: result.match?.lon,
          })
          onNext()
          return
        }
        // Low confidence — soft warning with the closest match as one-tap fix
        setSoftWarn({ suggestion: result.match })
        return
      } catch (err) {
        logger.warn('[Step3] Address verification failed, proceeding:', err)
        onNext()
        return
      } finally {
        setIsVerifying(false)
      }
    }

    onNext()
  }

  return (
    <div className="form-container">
      <form onSubmit={handleSubmit} className="form-inner py-12">
        <h2 className="text-h2 text-foreground mb-2">
          {t('step3.title')}
        </h2>
        {isItaly ? (
          // Italy: never echo the patient's actual name here
          <>
            <p className="text-body text-foreground mb-1">{t('step3.nameHeading')}</p>
            <p className="text-body text-muted-foreground mb-8">{t('step3.nameIntro')}</p>
          </>
        ) : (
          <p className="text-body text-foreground mb-8">
            {personalData.firstName} {personalData.lastName}
          </p>
        )}

        <div className="space-y-5">
          <h3 className="text-h3 text-foreground">{t('step3.subtitle')}</h3>

          <Select
            id="country"
            label={t('step3.country')}
            value={addressData.country}
            onChange={(e) => handleCountryChange(e.target.value)}
            options={countries}
          />

          <div className="grid grid-cols-3 gap-4">
            <div className="col-span-2">
              {addressFeatureEnabled ? (
                <AddressAutocomplete
                  id="address"
                  label={t('step3.address')}
                  placeholder={t('step3.addressPlaceholder')}
                  value={addressData.street || ''}
                  country={addressData.country}
                  error={errors.address}
                  onChange={(v) => { setStreetAndNumber({ street: v }); if (softWarn) setSoftWarn(null); if (errors.address) setErrors(prev => ({ ...prev, address: undefined })) }}
                  onBlur={() => handleBlur('address', addressData.street || '')}
                  onSelect={handleSuggestionSelect}
                />
              ) : (
                <Input
                  id="address"
                  type="text"
                  label={t('step3.address')}
                  placeholder={t('step3.addressPlaceholder')}
                  value={addressData.street || ''}
                  onChange={(e) => { setStreetAndNumber({ street: e.target.value }); if (errors.address) setErrors(prev => ({ ...prev, address: undefined })) }}
                  onBlur={(e) => handleBlur('address', e.target.value)}
                  error={errors.address}
                  icon={<MapPin className="w-5 h-5" />}
                  autoComplete="street-address"
                />
              )}
            </div>
            <Input
              id="houseNumber"
              type="text"
              label={t('step3.houseNumber')}
              placeholder={t('step3.houseNumberPlaceholder')}
              value={addressData.houseNumber || ''}
              disabled={!!addressData.noHouseNumber}
              onChange={(e) => { setStreetAndNumber({ houseNumber: e.target.value }); if (errors.houseNumber) setErrors(prev => ({ ...prev, houseNumber: undefined })) }}
              onBlur={(e) => handleBlur('houseNumber', e.target.value)}
              error={errors.houseNumber}
              autoComplete="address-line1"
            />
          </div>

          <label className="flex items-center gap-2 text-small text-muted-foreground cursor-pointer">
            <input
              type="checkbox"
              checked={!!addressData.noHouseNumber}
              onChange={(e) => {
                setStreetAndNumber({ noHouseNumber: e.target.checked })
                if (errors.houseNumber) setErrors(prev => ({ ...prev, houseNumber: undefined }))
              }}
              className="w-4 h-4 accent-primary"
            />
            {t('step3.noHouseNumber')}
          </label>

          {/* The number often ends up typed into the street instead of Nr. —
              advisory, since some street names genuinely end in a digit. */}
          {addressData.noHouseNumber && streetEndsWithHouseNumber(addressData.street || '') && (
            <p className="text-small text-amber-700 bg-amber-50 border border-amber-200 rounded-card p-3 -mt-1">
              {t('step3.houseNumberInStreet')}
            </p>
          )}

          <Input
            id="addressLine2"
            type="text"
            label={t('step3.addressLine2')}
            placeholder={t('step3.addressLine2Placeholder')}
            value={addressData.addressLine2}
            onChange={(e) => setAddressData({ addressLine2: e.target.value })}
            autoComplete="address-line2"
          />

          <Input
            id="postalCode"
            type="text"
            inputMode={POSTAL_CODE_CONFIGS[addressData.country]?.pattern?.source?.startsWith('^\\d') ? 'numeric' : 'text'}
            label={t('step3.postalCode')}
            placeholder={getPostalCodeExample(addressData.country)}
            value={addressData.postalCode}
            onChange={(e) => handleChange('postalCode', e.target.value)}
            onBlur={(e) => handleBlur('postalCode', e.target.value)}
            error={errors.postalCode}
            autoComplete="postal-code"
          />

          <Input
            id="city"
            type="text"
            label={t('step3.city')}
            placeholder={t('step3.cityPlaceholder')}
            value={addressData.city}
            onChange={(e) => handleChange('city', e.target.value)}
            onBlur={(e) => handleBlur('city', e.target.value)}
            error={errors.city}
            autoComplete="address-level2"
          />

          {usesProvinceDropdown ? (
            <Select
              id="province"
              label={t('step3.province')}
              value={addressData.province || ''}
              onChange={(e) => {
                // Selecting a province also derives the region into `state`;
                // the region itself is not shown to the patient.
                setAddressData({ province: e.target.value, state: regionForProvince(e.target.value) })
                if (errors.state) setErrors(prev => ({ ...prev, state: undefined }))
              }}
              options={[
                { value: '', label: t('common.pleaseSelect') },
                ...ITALIAN_PROVINCE_OPTIONS
              ]}
              error={errors.state}
            />
          ) : hasStateDropdown ? (
            <Select
              id="state"
              label={t('step3.state')}
              value={addressData.state}
              onChange={(e) => handleChange('state', e.target.value)}
              options={[
                { value: '', label: t('common.pleaseSelect') },
                ...availableStates
              ]}
              error={errors.state}
            />
          ) : (
            <Input
              id="state"
              type="text"
              label={t('step3.stateText')}
              placeholder={t('step3.statePlaceholder')}
              value={addressData.state}
              onChange={(e) => handleChange('state', e.target.value)}
              onBlur={(e) => handleBlur('state', e.target.value)}
              error={errors.state}
            />
          )}
        </div>

        {softWarn && (
          <InfoBox className="mt-6">
            <p className="font-medium mb-1">{t('addressCheck.title')}</p>
            <p className="mb-3">{t('addressCheck.body')}</p>
            {softWarn.suggestion && (
              <button
                type="button"
                onClick={applySuggestion}
                className="block w-full text-left px-3 py-2 mb-2 bg-white border border-border rounded-card hover:bg-muted/60 text-body"
              >
                <span className="flex items-start gap-2">
                  <MapPin className="w-4 h-4 flex-shrink-0 mt-0.5 text-primary" />
                  <span>{softWarn.suggestion.label}</span>
                </span>
              </button>
            )}
            <p className="text-small text-muted-foreground">{t('addressCheck.hint')}</p>
          </InfoBox>
        )}

        {isItaly && <ItalyDomicileFields />}

        <FormNavigation
          onBack={onBack}
          nextDisabled={!isValid || isVerifying}
          nextLabel={
            isVerifying ? t('addressCheck.checking')
            : softWarn ? t('addressCheck.useAnyway')
            : undefined
          }
        />
      </form>
    </div>
  )
}
